import './EditableMarkdownViewer.css'

import type { Optional } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { useState } from 'react'

import { type EditMode, HoverEditGroup } from '../hoverEditGroup/HoverEditGroup'
import { ConfigurationMarkdownViewer } from '../markdownviewer/ConfigurationMarkdownViewer'
import { MarkdownEditor } from './MarkdownEditor'

type Props = {
  value: Optional<string>
  onSave?: Optional<(newValue: Optional<string>) => void | Promise<void>>
}

export const EditableMarkdownViewer = ({ value, onSave }: Props) => {
  const [editMode, setEditMode] = useState<EditMode>()
  const [updatedValue, setUpdatedValue] = useState(value)

  const translate = useTranslate()

  const handleSaveContent = async () => {
    setEditMode(undefined)
    await onSave?.(updatedValue)
  }

  const handleCancelEditing = () => {
    setUpdatedValue(value)
    setEditMode(undefined)
  }

  return (
    <div
      className={`relative ${
        !!onSave && !editMode
          ? 'group/editor rounded-md p-2 bg-slate-100 dark:bg-slate-800'
          : ''
      }`}
    >
      {editMode ? (
        <>
          <MarkdownEditor
            value={updatedValue}
            onChange={(nextValue) => setUpdatedValue(nextValue)}
            options={{
              sideBySideFullscreen: editMode === 'modal',
              ...(editMode === 'custom' ? { toolbar: false } : {}),
            }}
          />
          <div className="absolute -bottom-2 left-0 flex flex-row gap-x-2">
            <Button variant="secondary" onClick={handleCancelEditing}>
              {translate('cancel')}
            </Button>
            <Button variant="primary" onClick={handleSaveContent}>
              {translate('save')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <ConfigurationMarkdownViewer
            shouldTransformLinksToDocSite
            className="min-h-[2em]"
          >
            {value ?? '\\-'}
          </ConfigurationMarkdownViewer>
          {!!onSave && (
            <HoverEditGroup
              onEdit={(mode) => setEditMode(mode)}
              isSimpleEditingAvailable
            />
          )}
        </>
      )}
    </div>
  )
}
